import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { TURN_CAPABLE_STATUSES, type ModelProvider } from '@reflex/shared';
import type {
  ProviderSecretManageAffordance,
  ProviderSecretRefreshAffordance,
  ProviderSubscriptionAffordance,
} from '@reflex/ui/components/model-providers/ModelProviderSecretPicker';
import type { Agent } from '@/lib/types';
import { apiErrorMessage } from '@/components/agent/agent-detail/api-error-copy';
import { useSwitchAgentSubscription } from '@/hooks/agents/useAgents';
import {
  useProviderSubscriptionAffordance,
  type ProviderSubscriptionWriteMode,
} from '@/components/model-providers/useProviderSubscriptionAffordance';

/**
 * The model-provider family whose subscriptions a given agent type can use, or
 * `null` when the type has no subscription auth (the on-box CLI is
 * provider-specific, so a swap can only stay within the same family).
 */
export function subscriptionProvider(agentType: string): ModelProvider | null {
  if (agentType === 'claude-code') return 'anthropic';
  if (agentType === 'codex') return 'openai';
  return null;
}

/**
 * Human copy for the machine-readable codes `switchSubscription` throws. The
 * API's error envelope carries the code in `error`, which the client surfaces
 * as both `ReflexApiError.code` and, absent a `message`, the error's `message` —
 * so a `snake_case` identifier is what a naive toast would show.
 */
const SWITCH_ERROR_COPY: Record<string, string> = {
  claude_max_needs_reconnect:
    'That Claude Max subscription needs to be reconnected first. Reconnect it, then switch again.',
  codex_subscription_needs_reconnect:
    'That Codex subscription needs to be reconnected first. Reconnect it, then switch again.',
  claude_max_credential_missing:
    'That Claude Max subscription is no longer available. Pick another one or add it again.',
  codex_subscription_credential_missing:
    'That Codex subscription is no longer available. Pick another one or add it again.',
  subscription_provider_mismatch:
    'That subscription is for a different provider than this agent. Pick one from the same provider.',
  provider_secret_not_subscription:
    'That key is an API key, not a subscription. Pick a subscription.',
  provider_secret_invalid: 'That subscription could not be read. Pick another one.',
  provider_secret_requires_user: 'This run has no owner to read subscriptions for.',
  no_active_organization: 'Pick an organization before switching subscriptions.',
  agent_not_switchable: 'This run has finished, so its subscription can no longer change.',
};

/**
 * Toast copy for a failed switch; see {@link apiErrorMessage}.
 *
 * Exported for tests; the hook is the only production caller.
 */
export function switchErrorMessage(err: unknown): string {
  return apiErrorMessage(err, SWITCH_ERROR_COPY, 'Could not switch the subscription. Try again.');
}

/** Tooltip and accessible name of the refresh control on the pinned row. */
const REFRESH_LABEL = 'Refresh this subscription on the devbox';

/**
 * Toast copy for a successful write. A refresh reports where the credential
 * landed, because that is the whole question the user clicked to answer: the
 * server writes it onto a running devbox immediately, and defers to the next
 * turn when the box is suspended (`credentialSyncPending`). A switch always
 * reads as "next turn" since that is when the CLI picks the credential up
 * either way.
 *
 * Exported for tests; the hook is the only production caller.
 */
export function switchSuccessMessage(updated: Agent | undefined, refresh: boolean): string {
  if (!refresh) return 'Subscription updated. It applies on the next turn.';
  return updated?.credentialSyncPending === true
    ? 'Devbox is suspended, so the refreshed subscription applies on the next turn.'
    : 'Subscription refreshed on the devbox.';
}

export interface SubscriptionSwitch {
  /** True when this agent can have its subscription switched right now. */
  canSwitch: boolean;
  /** The provider family whose subscriptions are offered (null when unsupported). */
  provider: ModelProvider | null;
  /** "Add a subscription" affordance to pass to the picker, wired to a dialog. */
  affordance: ProviderSubscriptionAffordance | undefined;
  /**
   * "Update this subscription" affordance for the picker's own subscription
   * rows, wired to the same dialog. Re-pasting the credential a run is pinned
   * to repairs that run — the write is forced, so the fresh token reaches the
   * box. Updating any *other* subscription leaves the run where it is.
   */
  manage: ProviderSecretManageAffordance | undefined;
  /** The registration dialog to mount adjacent to the picker. */
  dialog: ReactNode;
  /** Picker `onChange` handler: fires the switch mutation with toasts. */
  onChange: (providerSecretId: string | null) => void;
  /**
   * "Re-apply the pinned subscription" affordance for the picker's selected
   * row, or undefined when the run has no subscription to re-apply.
   */
  refresh: ProviderSecretRefreshAffordance | undefined;
  /**
   * True while a *switch* is in flight, which surfaces pass as the picker's
   * `disabled` so a second pick can't race the first. Deliberately false
   * during a refresh: that write leaves the selection alone, and a disabled
   * picker turns its popover into a read-only card, hiding the very row whose
   * spinner reports the refresh.
   */
  pending: boolean;
}

/**
 * Shared wiring for switching a running agent's model-provider subscription.
 * Consumed by both the agent detail panel selector and the chat-input toolbar
 * key icon so the two surfaces behave identically. Hooks are always called
 * (never conditionally) so this is safe to invoke before gating on
 * {@link SubscriptionSwitch.canSwitch}.
 */
export function useSubscriptionSwitch(agent: Agent): SubscriptionSwitch {
  const provider = subscriptionProvider(agent.agentType);
  const switchSubscription = useSwitchAgentSubscription();
  // Which kind of write is in flight. Both go through one mutation, but the
  // surfaces report them differently (see `SubscriptionSwitch.pending`).
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Send the switch. `force` re-sends even when the target is the subscription
   * already pinned, which is what a re-registration needs: the server re-resolves
   * the credential and pushes the fresh token onto the live box, so reconnecting
   * a dead subscription repairs the current run instead of silently no-opping.
   * `refresh` marks the forced re-send of the pinned subscription, which only
   * changes the toast copy and which control reports the pending state.
   */
  const submit = useCallback(
    (providerSecretId: string | null, { force = false, refresh = false } = {}) => {
      if (!providerSecretId) return;
      if (!force && providerSecretId === agent.modelProviderSecretId) return;
      if (refresh) setRefreshing(true);
      switchSubscription.mutate(
        { id: agent.id, providerSecretId },
        {
          onSuccess: (updated) => toast.success(switchSuccessMessage(updated, refresh)),
          onError: (err) => toast.error(switchErrorMessage(err)),
          onSettled: () => {
            if (refresh) setRefreshing(false);
          },
        },
      );
    },
    [agent.id, agent.modelProviderSecretId, switchSubscription],
  );

  const onChange = useCallback(
    (providerSecretId: string | null) => submit(providerSecretId),
    [submit],
  );

  // Called unconditionally; a no-op affordance for providers without a dialog.
  // Registering a subscription from inside the picker applies it to this run:
  // the user asked for it in the same gesture, so the write is theirs, unlike
  // the picker's own defaulting (which these surfaces opt out of via
  // `autoSelectDefault={false}`). Forced, because re-registering the
  // subscription the run is already on is the reconnect flow — the id is
  // unchanged but the credential is not. A null id means the server wrote no
  // selectable row, so this no-ops and the user picks from the list.
  //
  // Updating from a row's cog is not a choice of subscription, so it only
  // re-sends when it repaired the credential this run is already pinned to.
  // Otherwise the user fixing a stale subscription they weren't using would
  // find the run silently moved onto it.
  const onRegistered = useCallback(
    (providerSecretId: string | null, mode: ProviderSubscriptionWriteMode) => {
      if (mode === 'update' && providerSecretId !== agent.modelProviderSecretId) return;
      submit(providerSecretId, { force: true });
    },
    [submit, agent.modelProviderSecretId],
  );
  const { affordance, manage, dialog } = useProviderSubscriptionAffordance(
    provider ?? 'anthropic',
    {
      onRegistered,
    },
  );

  // Re-send the subscription the run is already on. Forced, because that is the
  // point: the id does not change, but the credential behind it does (the
  // server re-resolves it, refreshing an OAuth token near expiry, and writes it
  // onto the live box). This is the same repair the box performs for itself
  // when it wakes from suspend with a deferred switch — exposed as a button so
  // a run whose on-box credential went stale can be fixed without stopping it.
  const pinnedSecretId = agent.modelProviderSecretId ?? null;
  const refresh = useMemo<ProviderSecretRefreshAffordance | undefined>(
    () =>
      pinnedSecretId
        ? {
            label: REFRESH_LABEL,
            onRefresh: () => submit(pinnedSecretId, { force: true, refresh: true }),
            pending: refreshing,
          }
        : undefined,
    [pinnedSecretId, submit, refreshing],
  );

  return {
    canSwitch: !!provider && TURN_CAPABLE_STATUSES.has(agent.status),
    provider,
    affordance,
    manage,
    dialog,
    onChange,
    refresh,
    pending: switchSubscription.isPending && !refreshing,
  };
}
